import {Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatCheckboxModule} from "@angular/material/checkbox"
import {MAT_DIALOG_DATA} from "@angular/material/dialog"
import {MatTableModule} from "@angular/material/table"
import {DurationFromMillisecondsPipe} from "@common/pipes/duration/duration.pipe"
import {ContentTypeModel, ScanSubJobState} from "@generated"
import {
    ScanJobDialogGQL,
    ScanJobDialogSubJobAssignmentsGQL,
    ScanJobDialogUpdateSubJobGQL,
    ScanJobDialogSubJobFragment,
} from "@app/textures/texture-group-details/scan-job-dialog/scan-job-dialog.generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {FormatDatePipe} from "@common/pipes/format-date/format-date.pipe"
import {UtilsService} from "@legacy/helpers/utils"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {Labels} from "@labels"
import {FilesService} from "@common/services/files/files.service"
import {StateLabelComponent} from "@platform/components/shared/state-label/state-label.component"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {TypedMatCellDefDirective} from "@app/common/directives"
import {LoadingSpinnerComponent} from "@app/common/components/progress"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-scan-job-dialog",
    templateUrl: "./scan-job-dialog.component.html",
    styleUrls: ["./scan-job-dialog.component.scss"],
    imports: [
        MatTableModule,
        MatCheckboxModule,
        FormsModule,
        StateLabelComponent,
        FormatDatePipe,
        TypedMatCellDefDirective,
        DurationFromMillisecondsPipe,
        LoadingSpinnerComponent,
    ],
})
export class ScanJobDialogComponent implements OnInit {
    displayedColumns: string[] = ["id", "state", "active", "progress", "started", "completed", "duration", "tile_x", "tile_y", "log", "fitting", "assembly"]

    readonly permission = inject(PermissionsService)
    readonly destroyRef = inject(DestroyRef)
    readonly notifications = inject(NotificationsService)
    readonly refresh = inject(RefreshService)
    readonly utils = inject(UtilsService)
    $can = this.permission.$to
    readonly scanJobDialog = inject(ScanJobDialogGQL)
    readonly scanJobDialogSubJobAssignments = inject(ScanJobDialogSubJobAssignmentsGQL)
    readonly scanJobDialogUpdateSubJob = inject(ScanJobDialogUpdateSubJobGQL)

    scanJobId: string
    readonly $scanSubJobs = signal<ScanJobDialogSubJobFragment[]>([])
    readonly $loading = signal(true)
    readonly data = inject<{scanJobId: string}>(MAT_DIALOG_DATA)

    constructor() {
        this.scanJobId = this.data.scanJobId
    }

    ngOnInit(): void {
        this.refresh
            .keepFetched$(this.scanJobId, ContentTypeModel.ScanJob, () => fetchThrowingErrors(this.scanJobDialog)({id: this.scanJobId}))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((scanJob) => {
                this.$scanSubJobs.set(scanJob?.subJobs ?? [])
                this.$loading.set(false)
            })
    }

    refreshSubJobs() {
        this.refresh.item({id: this.scanJobId, __typename: ContentTypeModel.ScanJob})
    }

    updateItem(input: {active?: boolean; state?: ScanSubJobState}, scanSubJobId: string) {
        void this.notifications.withUserFeedback(
            async () => {
                await mutateThrowingErrors(this.scanJobDialogUpdateSubJob)({
                    input: {
                        active: input.active,
                        id: scanSubJobId,
                        state: input.state,
                    },
                })
                this.$scanSubJobs.set(
                    this.$scanSubJobs().map((subJob) => {
                        if (subJob.id === scanSubJobId) {
                            return {...subJob, active: input.active ?? subJob.active, state: input.state ?? subJob.state}
                        }
                        return subJob
                    }),
                )
            },
            {
                success: "Changes saved",
                error: "Cannot save changes",
            },
        )
    }

    private async downloadAssignment(scanSubJobId: string, type: "logAssignments" | "fittingAssignments" | "assemblyAssignments"): Promise<void> {
        const {scanSubJob} = await fetchThrowingErrors(this.scanJobDialogSubJobAssignments)({scanSubJobId})
        const url = scanSubJob?.[type]?.[0]?.dataObject.downloadUrl
        if (url) {
            FilesService.downloadFile("test", url)
        } else {
            this.notifications.showError("No file available for download")
        }
    }

    downloadLog(scanSubJob: ScanJobDialogSubJobFragment): void {
        void this.downloadAssignment(scanSubJob.id, "logAssignments")
    }

    downloadFitting(scanSubJob: ScanJobDialogSubJobFragment): void {
        void this.downloadAssignment(scanSubJob.id, "fittingAssignments")
    }

    downloadAssembly(scanSubJob: ScanJobDialogSubJobFragment): void {
        void this.downloadAssignment(scanSubJob.id, "assemblyAssignments")
    }

    scanSubJobDuration(startedAt: string, completedAt: string) {
        return new Date(completedAt).getTime() - new Date(startedAt).getTime()
    }

    protected readonly Labels = Labels
}
